// @mui material components
import Grid from "@mui/material/Grid";
import { useEffect, useMemo, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import apiClient from "../../../apiClient";

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    success: false,
    verificationRequests: 0,
    verificationCompletionRate: 0,
    completedCases: 0,
    pendingCases: 0,
    caseResolutionGraph: {
      labels: [],
      requested: [],
      resolved: [],
    },
  });
  const [loading, setLoading] = useState(true);

  const fetchPoliceDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/police/dashboard");
      const payload = response?.data || {};
      setDashboardData({
        success: Boolean(payload.success),
        verificationRequests: Number(payload.verificationRequests || 0),
        verificationCompletionRate: Number(payload.verificationCompletionRate || 0),
        completedCases: Number(payload.completedCases || 0),
        pendingCases: Number(payload.pendingCases || 0),
        caseResolutionGraph: {
          labels: payload.caseResolutionGraph?.labels || [],
          requested: payload.caseResolutionGraph?.requested || [],
          resolved: payload.caseResolutionGraph?.resolved || [],
        },
      });
    } catch (error) {
      console.error("Error fetching police dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoliceDashboard();
  }, []);

  const verificationRequestsChart = useMemo(
    () => ({
      labels: dashboardData.caseResolutionGraph.labels,
      datasets: {
        label: "Verification Requests",
        data: dashboardData.caseResolutionGraph.requested,
      },
    }),
    [dashboardData.caseResolutionGraph.labels, dashboardData.caseResolutionGraph.requested]
  );

  const completionRateChart = useMemo(() => {
    const { labels, requested, resolved } = dashboardData.caseResolutionGraph;
    const completionByMonth = requested.map((total, index) => {
      if (!total) return 0;
      return Number((((resolved[index] || 0) / total) * 100).toFixed(2));
    });

    return {
      labels,
      datasets: {
        label: "Completion Rate (%)",
        data: completionByMonth,
      },
    };
  }, [dashboardData.caseResolutionGraph]);

  const caseResolutionChart = useMemo(
    () => ({
      labels: dashboardData.caseResolutionGraph.labels,
      datasets: {
        label: "Resolved Cases",
        data: dashboardData.caseResolutionGraph.resolved,
      },
    }),
    [dashboardData.caseResolutionGraph.labels, dashboardData.caseResolutionGraph.resolved]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>Loading police dashboard...</MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Verification Requests"
                  description={`Total requests: ${dashboardData.verificationRequests}`}
                  date="Latest update"
                  chart={verificationRequestsChart}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Verification Completion Rate"
                  description={`${dashboardData.verificationCompletionRate}% overall completion`}
                  date="Latest update"
                  chart={completionRateChart}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="dark"
                  title="Case Resolution"
                  description={`Completed: ${dashboardData.completedCases} | Pending: ${dashboardData.pendingCases}`}
                  date="Latest update"
                  chart={caseResolutionChart}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
